import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faUser } from '@fortawesome/free-solid-svg-icons';
import { clinicsService, isPatientStatus, type PatientStatus } from '../../services/clinicsService';
import { GhostSelect } from '../../components/ui/GhostSelect';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { useNotifications } from '../../components/ui/notifications';
import EditorCard from '../../components/ui/EditorCard';

interface ClinicsNewPatientModalProps {
  onClose: () => void;
  // ESLint's base rule treats TypeScript call-signature parameters as runtime variables.
  // eslint-disable-next-line no-unused-vars
  onSuccess: (patientId?: number) => void;
}

const ClinicsNewPatientModal: React.FC<ClinicsNewPatientModalProps> = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [formData, setFormData] = useState<{
    full_name: string;
    email: string;
    phone: string;
    date_of_birth: string;
    gender: string;
    blood_type: string;
    status: PatientStatus;
  }>({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: 'other',
    blood_type: '',
    status: 'active',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const today = new Date().toLocaleDateString('en-CA');
  const normalizedName = formData.full_name.trim();
  const normalizedEmail = formData.email.trim();
  const normalizedPhone = formData.phone.trim();
  const emailIsValid = !normalizedEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
  const phoneDigits = normalizedPhone.replace(/\D/g, '');
  const phoneIsValid = !normalizedPhone || (phoneDigits.length >= 7 && phoneDigits.length <= 15);
  const hasContactMethod = Boolean(normalizedEmail || normalizedPhone);
  const birthDateIsValid = !formData.date_of_birth || formData.date_of_birth <= today;
  const isFormValid = normalizedName.length >= 2
    && hasContactMethod
    && emailIsValid
    && phoneIsValid
    && birthDateIsValid;

  const updateField = <K extends keyof typeof formData>(field: K, value: (typeof formData)[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (normalizedName.length < 2) {
      const message = t('clinics.patientForm.errors.nameRequired');
      setError(message);
      notifications.warning('Revisa los datos', message);
      return;
    }
    if (!hasContactMethod) {
      const message = t('clinics.patientForm.errors.contactRequired');
      setError(message);
      notifications.warning('Revisa los datos', message);
      return;
    }
    if (!emailIsValid) {
      const message = t('clinics.patientForm.errors.emailInvalid');
      setError(message);
      notifications.warning('Revisa el correo', message);
      return;
    }
    if (!phoneIsValid) {
      const message = t('clinics.patientForm.errors.phoneInvalid');
      setError(message);
      notifications.warning('Revisa el teléfono', message);
      return;
    }
    if (!birthDateIsValid) {
      const message = t('clinics.patientForm.errors.birthDateInvalid');
      setError(message);
      notifications.warning('Revisa la fecha', message);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const payload: any = { ...formData };
      if (!payload.date_of_birth) delete payload.date_of_birth;
      if (!payload.blood_type) delete payload.blood_type;
      if (!payload.phone) delete payload.phone;
      if (!payload.email) delete payload.email;

      const createdPatient = await clinicsService.createPatient(payload);
      notifications.success('Paciente creado', `${normalizedName} se añadió correctamente.`);
      onSuccess(createdPatient.id);
    } catch (err: any) {
      const message = err.response?.data?.detail || t('clinics.patientForm.errors.createFailed');
      setError(message);
      notifications.error('No se pudo crear el paciente', typeof message === 'string' ? message : undefined);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-ui="clinics-new-patient-modal-region-001" className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div data-ui="clinics-new-patient-modal-region-002" className="absolute inset-0 bg-black/60 backdrop-filter backdrop-blur-xl transition-opacity" onClick={onClose} />
      <EditorCard
        dataUi="clinics-new-patient-modal-region-003"
        headerDataUi="clinics-new-patient-modal-region-004"
        bodyDataUi="clinics-new-patient-modal-region-005"
        footerDataUi="clinics-new-patient-modal-region-007"
        dataTour="create-patient-modal"
        title={t('clinics.modals.newPatient.title')}
        icon={<FontAwesomeIcon icon={faUser} className="text-green-400" />}
        onClose={onClose}
        closeLabel={t('common.close', 'Cerrar')}
        className="animate-fade-in-up"
        footer={(
          <>
            <BtnTaratic type="button" onClick={onClose} color="ghost-dark" data-ui="clinics-new-patient-modal-cancel">
              {t('clinics.modals.newPatient.cancel')}
            </BtnTaratic>
            <BtnTaratic
              type="submit"
              form="new-patient-form"
              color="green"
              disabled={loading || !isFormValid}
              data-ui="clinics-new-patient-modal-save"
              className="flex items-center gap-2"
            >
              {loading ? (
                <div data-ui="clinics-new-patient-modal-region-008" className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faSave} />
              )}
              {t('clinics.modals.newPatient.save')}
            </BtnTaratic>
          </>
        )}
      >
          {error && (
            <div data-ui="clinics-new-patient-modal-region-006" className="mb-6 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}

          <form id="new-patient-form" onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="md:col-span-2 grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.fullName')}</span>
              <input
                type="text"
                required
                minLength={2}
                maxLength={200}
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={formData.full_name}
                onChange={e => updateField('full_name', e.target.value)}
                placeholder={t('clinics.patientForm.placeholders.fullName')}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.email')}</span>
              <input
                type="email"
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={formData.email}
                onChange={e => updateField('email', e.target.value)}
                placeholder={t('clinics.patientForm.placeholders.email')}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.phone')}</span>
              <input
                type="tel"
                maxLength={20}
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={formData.phone}
                onChange={e => updateField('phone', e.target.value)}
                placeholder={t('clinics.patientForm.placeholders.phone')}
              />
            </label>

            <p className="md:col-span-2 -mt-1 text-[11px] text-white/60">
              {t('clinics.patientForm.contactHint')}
            </p>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.birthDate')}</span>
              <input
                type="date"
                max={today}
                className="border border-white/20 rounded-md px-3 py-2 bg-black/20 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                value={formData.date_of_birth}
                onChange={e => updateField('date_of_birth', e.target.value)}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.gender')}</span>
              <GhostSelect
                value={formData.gender}
                onChange={(val: string) => updateField('gender', val)}
                className="w-full"
                options={[
                  { value: "male", label: t('clinics.patientForm.options.gender.male') },
                  { value: "female", label: t('clinics.patientForm.options.gender.female') },
                  { value: "other", label: t('clinics.patientForm.options.gender.other') }
                ]}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.bloodType')}</span>
              <GhostSelect
                value={formData.blood_type}
                onChange={(val: string) => updateField('blood_type', val)}
                className="w-full"
                options={[
                  { value: "", label: t('clinics.patientForm.options.bloodType.notSpecified') },
                  { value: "A+", label: "A+" },
                  { value: "A-", label: "A-" },
                  { value: "B+", label: "B+" },
                  { value: "B-", label: "B-" },
                  { value: "AB+", label: "AB+" },
                  { value: "AB-", label: "AB-" },
                  { value: "O+", label: "O+" },
                  { value: "O-", label: "O-" }
                ]}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs text-white/80">{t('clinics.patientForm.labels.status')}</span>
              <GhostSelect
                value={formData.status}
                onChange={(val: string) => {
                  if (isPatientStatus(val)) updateField('status', val);
                }}
                className="w-full"
                options={[
                  { value: "active", label: t('clinics.patientForm.options.status.active') },
                  { value: "inactive", label: t('clinics.patientForm.options.status.inactive') },
                  { value: "discharged", label: t('clinics.patientForm.options.status.discharged') }
                ]}
              />
            </label>
          </form>
      </EditorCard>
    </div>
  );
};

export default ClinicsNewPatientModal;
